import LogoutButton from '@e3dc-react/shell/Elements/Buttons/Settings/LogoutButton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Avatar, Box, Chip, Menu, MenuItem, Theme, Typography, useMediaQuery } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'
import useModals from 'src/Hooks/useModals'
import { useUser } from 'src/Hooks/useUser'
import { makeStyles } from 'tss-react/mui'
import i18next from 'i18next'
import { languages } from '@e3dc-react/shell/Configuration'

interface StyleProps {
  onUserProfile: boolean
  hasBackground: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  chip: {
    height: 48,
    borderRadius: 36,
    border: props.onUserProfile ? '1px solid' : '0',
    borderColor: props.onUserProfile ? theme.palette.primary.main : 'inherit',
    backgroundColor: props.onUserProfile ? 'inherit' : theme.palette.grey.grey06,
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      height: 70,
      border: 0,
      backgroundColor: props.onUserProfile || !props.hasBackground ? 'transparent' : theme.palette.primary.dark,
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  avatar: {
    '&.MuiChip-avatar': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      height: 42,
      width: 42,
      flexDirection: 'column',
      fontSize: 18,
      fontWeight: 600,
      [theme.breakpoints.down('md')]: {
        height: 62,
        width: 62,
        backgroundColor: props.onUserProfile ? theme.palette.primary.light : theme.palette.primary.main,
        color: props.onUserProfile ? theme.palette.grey.grey00 : theme.palette.primary.contrastText,
      },
    },
  },
  chipContent: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'start',
      marginLeft: theme.spacing(1),
    },
  },
  chipTypography: {
    textAlign: 'left',
    color: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      color: theme.palette.grey.grey00,
    },
  },
  chipSubtitle: {
    textAlign: 'left',
    color: theme.palette.common.white,
    [theme.breakpoints.up('md')]: {
      color: theme.palette.grey.grey03,
    },
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    height: '36px',
  },
  iconButton: {
    padding: 0,
  },
}))

interface UserBadgeProps {
  onUserProfileClick: () => void
  hasMenu?: boolean
  hasBackground?: boolean
}

const UserBadge: React.FC<UserBadgeProps> = ({ onUserProfileClick, hasMenu = true, hasBackground = true }) => {
  const { userData } = useUser()
  const { t } = useCustomTranslation()
  const { openLogoutModal, openLanguageModal } = useModals()
  const location = useLocation()
  const { classes } = useStyles({ onUserProfile: location.pathname === '/user', hasBackground })
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const [chipAnchor, setChipAnchor] = useState<HTMLButtonElement | null>(null)
  const selectedLanguage = languages.find(language => language.shortName === i18next.language)
  const firstLetter = userData?.Vorname?.slice(0, 1).toUpperCase()
  const secondLetter = userData?.Name?.slice(0, 1).toUpperCase()
  const userLevel = userData?.level ?? null

  const fullName = userData?.Vorname && userData?.Name ? `${userData?.Vorname} ${userData?.Name}` : userData?.Vorname ?? userData?.Name ?? null

  const avatarContent =
    firstLetter && secondLetter ? (
      <>
        <Box>{firstLetter}</Box>
        <Box>{secondLetter}</Box>
      </>
    ) : (
      firstLetter ?? secondLetter ?? null
    )

  const ChipContent = (
    <Box className={classes.chipContent}>
      <Typography className={classes.chipTypography} variant={mdUp ? 'subtitle2' : 'h5'}>
        {fullName}
      </Typography>
      <Typography className={classes.chipSubtitle} variant={mdUp ? 'caption' : 'subtitle2'}>
        {userLevel ? t(`framework.userBadge.userlevel.${userLevel}`) : null}
      </Typography>
    </Box>
  )

  const ChipAvatar = (
    <Avatar
      classes={{
        root: classes.avatar,
      }}
    >
      {avatarContent}
    </Avatar>
  )

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      hasMenu ? setChipAnchor(event?.currentTarget) : onUserProfileClick()
    },
    [hasMenu, onUserProfileClick]
  )

  const handleMenuClose = useCallback(() => {
    setChipAnchor(null)
  }, [])

  const handleUserProfileClick = useCallback(() => {
    setChipAnchor(null)
    onUserProfileClick()
  }, [onUserProfileClick])

  const handleLogoutClick = useCallback(() => {
    openLogoutModal()
    setChipAnchor(null)
  }, [openLogoutModal])

  const handleLanguageClick = useCallback(() => {
    openLanguageModal()
    setChipAnchor(null)
  }, [openLanguageModal])

  return (
    <>
      <Chip
        className={classes.iconButton}
        onClick={handleMenuOpen}
        data-qa={'user-badge-menu-opener'}
        classes={{ root: classes.chip }}
        label={ChipContent}
        avatar={ChipAvatar}
        component={'button'}
        /** "If true, the chip will appear clickable, and will raise when pressed, even if the onClick prop is not defined..."  */
        clickable={hasBackground}
      />
      {hasMenu && (
        <Menu id="userBadge-menu" open={!!chipAnchor} onClose={handleMenuClose} anchorEl={chipAnchor} disableScrollLock={true}>
          <MenuItem className={classes.menuItem} onClick={handleUserProfileClick} data-qa="user-profile-link">
            <Typography>{t('user.title')}</Typography>
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={handleLanguageClick} data-qa="language-selection-link">
            <Box
              component="img"
              src={selectedLanguage?.icon}
              alt={i18next.language?.toUpperCase()}
              sx={{ height: '12px', width: '12px', borderRadius: '12px', objectFit: 'cover' }}
            />
            <Typography>{t('common.language')}</Typography>
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={handleLogoutClick}>
            <LogoutButton text={t('common.logout')} />
          </MenuItem>
        </Menu>
      )}
    </>
  )
}

export default UserBadge
